import axios from 'axios';
import { getApiBase } from '../utils/baseUrl';

const BASE = getApiBase();
const API_CACHE_PREFIX = 'glovy_api_cache_v1:';
const pendingGets = new Map();

const readCache = (key, ttlMs) => {
  try {
    const raw = sessionStorage.getItem(API_CACHE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > ttlMs) return null;
    return parsed.data;
  } catch {
    return null;
  }
};

const writeCache = (key, data) => {
  try {
    sessionStorage.setItem(API_CACHE_PREFIX + key, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // Ignore storage quota/private mode issues.
  }
};

const clearCacheByPrefix = (prefix) => {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(API_CACHE_PREFIX + prefix)) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // Ignore storage access failures.
  }
};

const cachedGet = async (url, key, ttlMs = 120000) => {
  const cached = readCache(key, ttlMs);
  if (cached != null) {
    return cached;
  }
  const inFlightKey = `${key}|${url}`;
  if (pendingGets.has(inFlightKey)) {
    return pendingGets.get(inFlightKey);
  }

  const req = api.get(url)
    .then(response => {
      writeCache(key, response.data);
      return response.data;
    })
    .finally(() => {
      pendingGets.delete(inFlightKey);
    });

  pendingGets.set(inFlightKey, req);
  return req;
};

// No withCredentials — we use JWT Bearer tokens, not cookies
const api = axios.create({
  baseURL: BASE,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT to every request
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('glovy_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Clear stale auth on unauthorized responses
api.interceptors.response.use(r => r, err => {
  if (err.response?.status === 401 || err.response?.status === 403) {
    localStorage.removeItem('glovy_token');
    localStorage.removeItem('glovy_user');
    window.dispatchEvent(new Event('glovy-auth-cleared'));
  }
  return Promise.reject(err);
});

export const authAPI = {
  register: d => api.post('/auth/register', d).then(r => r.data),
  login: (identifier, password) => api.post('/auth/login', { identifier, email: identifier, password }).then(r => r.data),
};
export const productAPI = {
  getAll:     ()       => cachedGet('/products', 'products:all', 120000),
  getById:    id       => cachedGet(`/products/${id}`, `products:id:${id}`, 300000),
  getFeatured:()       => cachedGet('/products/featured', 'products:featured', 120000),
  search:     q        => cachedGet(`/products/search?q=${encodeURIComponent(q)}`, `products:search:${q || ''}`, 60000),
  filter:     (s, c)   => {
    const p = new URLSearchParams();
    if (s) p.append('style', s);
    if (c) p.append('category', c);
    const q = p.toString();
    return cachedGet(`/products/filter?${q}`, `products:filter:${q}`, 120000);
  },
  create:     fd       => api.post('/products', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => { clearCacheByPrefix('products:'); return r.data; }),
  update:     (id, fd) => api.put(`/products/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => { clearCacheByPrefix('products:'); return r.data; }),
  delete:     id       => api.delete(`/products/${id}`).then(r => { clearCacheByPrefix('products:'); return r.data; }),
};
export const cartAPI = {
  get:       uid           => api.get(`/cart/${uid}`).then(r => r.data),
  add:       (uid,pid,qty=1) => api.post(`/cart/${uid}/add?productId=${pid}&qty=${qty}`).then(r => r.data),
  updateQty: (uid,iid,q)   => api.put(`/cart/${uid}/item/${iid}`, { quantity: q }).then(r => r.data),
  remove:    (uid,iid)     => api.delete(`/cart/${uid}/item/${iid}`).then(r => r.data),
  clear:     uid           => api.delete(`/cart/${uid}/clear`).then(r => r.data),
};
export const orderAPI = {
  getUserOrders: uid  => api.get(`/orders/user/${uid}`).then(r => r.data),
  getById:       id   => api.get(`/orders/${id}`).then(r => r.data),
  checkout:      body => api.post('/orders/checkout', body).then(r => r.data),
  updateStatus:  (id,status) => api.patch(`/orders/${id}/status`, { status }).then(r => r.data),
};
export const paymentAPI = {
  createIntent: uid => api.post(`/api/payments/create-intent?userId=${uid}`).then(r => r.data),
  verify: payload => api.post('/api/payments/verify', payload).then(r => r.data),
};
export const userAPI = {
  getProfile:     id      => api.get(`/users/${id}`).then(r => r.data),
  updateProfile:  (id, d) => api.put(`/users/${id}`, d).then(r => r.data),
  changePassword: (id,o,n)=> api.post(`/users/${id}/change-password`, { oldPassword:o, newPassword:n }).then(r => r.data),
  getAddresses:   id      => api.get(`/users/${id}/addresses`).then(r => r.data),
  addAddress:     (id, d) => api.post(`/users/${id}/addresses`, d).then(r => r.data),
  deleteAddress:  (id,aid)=> api.delete(`/users/${id}/addresses/${aid}`).then(r => r.data),
};
export const wishlistAPI = {
  get:    uid       => api.get(`/wishlist/${uid}`).then(r => r.data),
  add:    (uid,pid) => api.post(`/wishlist/${uid}/add?productId=${pid}`).then(r => r.data),
  remove: (uid,pid) => api.delete(`/wishlist/${uid}/remove?productId=${pid}`).then(r => r.data),
};
export const adminAPI = {
  getStats:  () => api.get('/admin/stats').then(r => r.data),
  getUsers:  () => api.get('/admin/users').then(r => r.data),
  getOrders: () => api.get('/admin/orders').then(r => r.data),
};
export const reviewAPI = {
  create:           d       => api.post('/reviews', d).then(r => r.data),
  getProductReviews:(pid)   => api.get(`/reviews/product/${pid}`).then(r => r.data),
  getProductRating: (pid)   => api.get(`/reviews/product/${pid}/rating`).then(r => r.data),
  getUserReviews:   (uid)   => api.get(`/reviews/user/${uid}`).then(r => r.data),
  getById:          id      => api.get(`/reviews/${id}`).then(r => r.data),
  delete:           id      => api.delete(`/reviews/${id}`).then(r => r.data),
};
// Convenience exports for direct use
export const createReview = d => reviewAPI.create(d);
export const getProductReviews = pid => reviewAPI.getProductReviews(pid);
export const getProductRating = pid => reviewAPI.getProductRating(pid);
export const getUserReviews = uid => reviewAPI.getUserReviews(uid);
export default api;